import {
  DiscountGroupType,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-discounts-common/discount";
import {
  DiscountPurchaseType,
  DynamicRangeExpirationUnits,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { assertNever } from "@redotech/util/type";
import { RedoListItem } from "../../arbiter-components/list/redo-list";

export const purchaseTypeOptions: RedoListItem<DiscountPurchaseType>[] = [
  {
    id: DiscountPurchaseType.ONE_TIME,
    type: "text",
    text: "One-time purchase",
    value: DiscountPurchaseType.ONE_TIME,
  },
  {
    id: DiscountPurchaseType.SUBSCRIPTION,
    type: "text",
    text: "Subscription",
    value: DiscountPurchaseType.SUBSCRIPTION,
  },
  {
    id: DiscountPurchaseType.BOTH,
    type: "text",
    text: "Both",
    value: DiscountPurchaseType.BOTH,
  },
];

export const recurringCycleLimitOptions = [
  { id: "first", label: "Limit discount to the first payment" },
  { id: "multiple", label: "Limit discount to multiple recurring payments" },
  { id: "all", label: "Discount applies to all recurring payments" },
];

export const discountValueTypeItems: RedoListItem<DiscountValueType>[] = [
  {
    id: DiscountValueType.PERCENTAGE,
    type: "text",
    text: "%",
    value: DiscountValueType.PERCENTAGE,
  },
  {
    id: DiscountValueType.AMOUNT,
    type: "text",
    text: "$",
    value: DiscountValueType.AMOUNT,
  },
];

const prettyDiscountType = (type: DiscountGroupType) => {
  switch (type) {
    case DiscountGroupType.DYNAMIC:
      return "Dynamic";
    case DiscountGroupType.STATIC:
      return "Static";
    default:
      assertNever(type);
  }
};

export const discountGroupTypeOptions: RedoListItem<DiscountGroupType>[] =
  Object.values(DiscountGroupType).map((type) => ({
    id: type,
    type: "text",
    value: type,
    text: prettyDiscountType(type),
  }));

export const dynamicRangeExpirationUnitsOptions: RedoListItem<DynamicRangeExpirationUnits>[] =
  [
    { id: DynamicRangeExpirationUnits.MINUTES, text: "Minutes" },
    { id: DynamicRangeExpirationUnits.HOURS, text: "Hours" },
    { id: DynamicRangeExpirationUnits.DAYS, text: "Days" },
    { id: DynamicRangeExpirationUnits.WEEKS, text: "Weeks" },
  ].map((item) => ({ ...item, type: "text" as const, value: item.id }));

export const discountCodeNameTitle: Record<DiscountGroupType, string> = {
  [DiscountGroupType.STATIC]: "Discount code",
  [DiscountGroupType.DYNAMIC]: "Discount code prefix",
};

export function getDiscountCodeStrategyDescription({
  prefix,
  type,
}: {
  prefix: string;
  type: DiscountGroupType;
}) {
  switch (type) {
    case DiscountGroupType.STATIC:
      return `The code ${prefix.toUpperCase()} will be sent to all customers.`;
    case DiscountGroupType.DYNAMIC:
      return `A unique discount code with this prefix will be generated for each customer. Eg '${prefix.toUpperCase()}-12345'`;
    default:
      assertNever(type);
  }
}

export function getDefaultExpiration(expirationType: ExpirationType) {
  switch (expirationType) {
    case ExpirationType.NEVER:
    case ExpirationType.DELIVERY:
      return { expirationType };
    case ExpirationType.EXPIRATION_DAYS:
      return { expirationType, days: 7 };
    case ExpirationType.DATE:
      return { expirationType, date: new Date() };
    default:
      assertNever(expirationType);
  }
}
